import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, FolderKanban, Calendar as CalendarIcon, Building } from 'lucide-react';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import CentresManager from '../components/CentresManager';

const emptyProject = {
  name: '',
  code: '',
  description: '',
  start_date: '',
  end_date: '',
  status: 'active',
  remarks: '',
};

const Projects = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [form, setForm] = useState(emptyProject);
  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });
  const [centresDlg, setCentresDlg] = useState({ open: false, project: null });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get('/projects');
      setItems(r.data || []);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setForm(emptyProject); setDlg({ open: true, editing: null }); };
  const openEdit = (p) => {
    setForm({
      name: p.name || '',
      code: p.code || '',
      description: p.description || '',
      start_date: p.start_date ? new Date(p.start_date).toISOString().slice(0, 10) : '',
      end_date: p.end_date ? new Date(p.end_date).toISOString().slice(0, 10) : '',
      status: p.status || 'active',
      remarks: p.remarks || '',
    });
    setDlg({ open: true, editing: p });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    try {
      const payload = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      if (dlg.editing) {
        await api.put(`/projects/${dlg.editing.id}`, payload);
        toast.success('Project updated');
      } else {
        await api.post('/projects', payload);
        toast.success('Project added');
      }
      setDlg({ open: false, editing: null });
      setForm(emptyProject);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/projects/${deleteDlg.item.id}`);
      toast.success('Project deleted');
      setDeleteDlg({ open: false, item: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="projects-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <FolderKanban className="w-9 h-9 text-emerald-700" />
            Projects
          </h1>
          <p className="text-lg text-slate-600">Manage umbrella schemes / projects (e.g., SVEP, NRLM, DAY-NRLM)</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="add-project-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <FolderKanban className="w-16 h-16 mx-auto text-slate-300 mb-3" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No projects yet</h3>
          <p className="text-slate-500">Click "Add Project" to create your first one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Start</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">End</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p, i) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid="project-row">
                  <td className="px-4 py-3 text-sm text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{p.name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-700">{p.code || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{p.start_date ? new Date(p.start_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{p.end_date ? new Date(p.end_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      p.status === 'active'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-200 text-slate-700'
                    }`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={p.description}>{p.description || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setCentresDlg({ open: true, project: p })} className="h-7 px-2 text-emerald-700 hover:bg-emerald-50" data-testid={`manage-centres-${p.id}`}>
                        <Building className="w-3.5 h-3.5 mr-1" /> Centres
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" data-testid={`edit-project-${p.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteDlg({ open: true, item: p })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-project-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dlg.open} onOpenChange={(o) => { if (!o) { setDlg({ open: false, editing: null }); setForm(emptyProject); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dlg.editing ? 'Edit Project' : 'Add Project'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g., SVEP" data-testid="project-name" />
              </div>
              <div>
                <Label>Code (short)</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g., SVEP-01" />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDlg({ open: false, editing: null })}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-project-btn">
                {dlg.editing ? 'Update' : 'Add Project'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDelete}
        title="Delete Project"
        description="This action cannot be undone."
        itemName={deleteDlg.item?.name || ''}
      />

      <CentresManager
        open={centresDlg.open}
        onOpenChange={(o) => setCentresDlg({ open: o, project: o ? centresDlg.project : null })}
        project={centresDlg.project}
      />
    </div>
  );
};

export default Projects;
